import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FileText, DollarSign, HelpCircle, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import heroFaq from "@/assets/hero-faq-illustration.jpg";
import { useCountUp } from "@/hooks/useCountUp";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { HeroImage } from "@/components/HeroImage";

const FAQ = () => {
  const { ref: pricingRef, isVisible: pricingVisible } = useScrollAnimation(0.2);
  const priceCount = useCountUp(100, 2000, pricingVisible);
  
  const whatToBring = [
    "W-2s from all employers",
    "1099 forms (interest, dividends, contractor income)",
    "Government-issued ID",
    "Business income & expense records (if sole proprietor)",
    "Deduction information (mortgage interest, charitable donations, etc.)",
  ];

  const processSteps = [
    { number: "1", title: "Book an appointment", description: "Choose a convenient time online" },
    { number: "2", title: "Share your documents", description: "Bring or email your tax documents" },
    { number: "3", title: "Review & approve", description: "We'll go through everything together" },
    { number: "4", title: "File & relax", description: "Your return is filed securely and quickly" },
  ];

  const faqs = [
    {
      question: "Do you handle corporations or partnerships?",
      answer: "No — I specialize exclusively in individuals and sole proprietors to provide the best possible service in those areas.",
    },
    {
      question: "Can I have a virtual appointment?",
      answer: "Yes! I offer both in-person and virtual appointments to accommodate your schedule and preferences.",
    },
    {
      question: "What's the turnaround time?",
      answer: "Most returns are completed within 1-3 business days after receiving all necessary documents.",
    },
    {
      question: "What if I receive an IRS letter?",
      answer: "I'll help you understand and respond to any IRS correspondence related to returns I've prepared.",
    },
    {
      question: "Can you file amended returns?",
      answer: "Yes, I can help you file an amended return if you need to make corrections to a previously filed return.",
    },
    {
      question: "Do you offer year-round support?",
      answer: "Absolutely! I'm available throughout the year for tax planning questions and support.",
    },
    {
      question: "I sold my home - what do I need to bring to my tax appointment?",
      answer: "You will most likely not need to list your home sale unless: 1) you run a business in your home, 2) you made greater than $600K profit on the sale, or 3) you lived in the home for less than 2 years.\n\nIf you need to file a sale of residence, you will need the following:\n• Purchase and sale papers from the house you sold\n• Receipts for any repairs over the time lived in that you paid for\n• If you purchased another home, the closing papers and expenses of buying",
    },
    {
      question: "Can I claim my grown child who is not in school and is not working if I am supporting them?",
      answer: "To claim someone as a dependent, they must meet specific IRS requirements:\n\nGeneral Rules for Dependents:\n• Must be a U.S. citizen, resident alien, or national, or a resident of Canada or Mexico\n• Can't be claimed on more than one tax return\n• Can't claim a dependent on their own return\n• You can't claim your spouse as a dependent if filing jointly\n• Must be a qualifying child or qualifying relative\n\nQualifying Child Tests:\n• Relationship: Your son, daughter, stepchild, foster child, sibling, or descendant of these\n• Age: Under 19, or under 24 if a full-time student, or any age if permanently and totally disabled\n• Residency: Lives with you for more than half the year\n• Support: Gets more than half their financial support from you\n• Joint Return: Not filing as married filing jointly\n\nQualifying Relative Tests:\n• Not a qualifying child of any taxpayer\n• Lives with you all year or is a specific type of relative\n• Gross income under $5,050\n• Gets more than half their financial support from you\n\nFor a grown child not in school and not working, they could qualify as a qualifying relative if they meet these tests.",
    },
  ];

  return (
    <Layout>
      {/* Header */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src={heroFaq}
            alt="Tax professional answering client questions"
            priority={true}
            width={1920}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        
        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">Your Questions Answered</h1>
            <p className="text-xl text-primary-foreground/90">
              Everything you need to know about pricing, process, and what to expect
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className="py-8 md:py-12 bg-gradient-to-b from-background via-muted/20 to-background relative overflow-hidden">
        {/* Animated blobs background */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="absolute inset-0 opacity-5 dot-grid"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">

            <div className="relative">
              {/* Featured Pricing Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                <div className="px-6 py-2 bg-gradient-to-r from-accent via-secondary to-accent rounded-full text-white font-bold text-sm shadow-xl animate-pulse-glow">
                  💎 Transparent Pricing
                </div>
              </div>
              
              <Card className="p-10 bg-gradient-to-br from-accent/20 via-secondary/10 to-accent/20 border-4 border-accent/40 shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden group">
                {/* Sparkle decorations */}
                <div className="absolute top-4 right-4 w-4 h-4 bg-accent rounded-full animate-twinkle"></div>
                <div className="absolute bottom-6 left-6 w-3 h-3 bg-secondary rounded-full animate-twinkle animation-delay-2000"></div>
                <div className="absolute top-1/2 right-10 w-2 h-2 bg-accent/60 rounded-full animate-twinkle animation-delay-4000"></div>
                
                {/* Shine sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
                
                {/* Radial glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-accent via-secondary to-accent opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                
                <div className="relative z-10 text-center">
                  <p className="text-xl text-muted-foreground mb-6 font-medium">
                    Most individual returns start at
                  </p>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-muted-foreground text-2xl">$</span>
                    <span className="font-black text-7xl bg-gradient-to-br from-accent via-secondary to-accent bg-clip-text text-transparent animate-shimmer bg-[length:200%_100%]">
                      {pricingVisible ? priceCount : 0}
                    </span>
                  </div>
                  <div className="h-1 w-24 mx-auto bg-gradient-to-r from-transparent via-accent to-transparent mb-4"></div>
                  <p className="text-muted-foreground mb-3 text-lg">
                    Sole proprietor returns vary based on complexity
                  </p>
                  <div className="inline-block px-4 py-2 bg-accent/10 rounded-full border border-accent/20">
                    <p className="text-sm text-accent font-semibold">
                      Quotes provided upfront — no surprises!
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16 bg-gradient-to-br from-muted/30 via-background to-muted/30 relative overflow-hidden">
        {/* Animated blobs background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-20 w-80 h-80 bg-secondary/30 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/3 right-1/3 w-72 h-72 bg-accent/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="absolute inset-0 opacity-5 dot-grid"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-full mb-4">
                <FileText className="h-12 w-12 text-secondary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                What to Bring
              </h2>
              <p className="text-muted-foreground text-lg">Make sure you have these documents ready</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {whatToBring.map((item, index) => (
                <Card 
                  key={index} 
                  className="group relative p-6 hover:shadow-2xl hover:shadow-secondary/20 transition-all duration-500 hover:-translate-y-2 border-2 hover:border-secondary/50 bg-gradient-to-br from-card via-card/80 to-card/50 animate-fade-in overflow-hidden"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {/* Sparkle decorations */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-secondary rounded-full animate-twinkle"></div>
                  <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-accent rounded-full animate-twinkle animation-delay-2000"></div>
                  
                  {/* Shine sweep */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  {/* Radial glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 via-accent/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
                  
                  <div className="flex items-start gap-4 relative z-10">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-secondary via-secondary to-accent text-white flex items-center justify-center text-base font-bold flex-shrink-0 shadow-xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 group-hover:shadow-secondary/50">
                      {index + 1}
                    </div>
                    <p className="font-semibold text-base group-hover:text-secondary transition-colors pt-2 leading-relaxed">{item}</p>
                  </div>
                </Card>
              ))}
              
              {/* CTA Card - New Client */}
              <Link to="/new-client" className="block">
                <Card 
                  className="group relative p-6 hover:shadow-2xl hover:shadow-accent/30 transition-all duration-500 hover:-translate-y-2 border-2 hover:border-accent/60 bg-gradient-to-br from-accent/10 via-accent/5 to-card/50 animate-fade-in overflow-hidden h-full"
                  style={{ animationDelay: '400ms' }}
                >
                  {/* Sparkle decorations */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-accent rounded-full animate-twinkle"></div>
                  <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-secondary rounded-full animate-twinkle animation-delay-2000"></div>
                  
                  {/* Shine sweep */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-accent/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  {/* Radial glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-secondary/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
                  
                  <div className="flex flex-col items-center justify-center gap-4 relative z-10 h-full min-h-[80px]">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent via-accent to-secondary text-white flex items-center justify-center shadow-xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 group-hover:shadow-accent/50">
                      <ArrowRight className="h-5 w-5" />
                    </div>
                    <p className="font-bold text-base text-center group-hover:text-accent transition-colors leading-relaxed">
                      New Client?<br />
                      <span className="text-sm font-semibold">Start Here →</span>
                    </p>
                  </div>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        {/* Animated blobs background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-96 h-96 bg-primary/30 rounded-full blur-3xl animate-blob"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/30 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-accent/20 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
        </div>
        <div className="absolute inset-0 opacity-5 dot-grid"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-flex items-center justify-center p-6 bg-gradient-to-br from-cyan-100/30 to-blue-100/20 rounded-full mb-4 shadow-lg">
                <img src="/images/logo-tiny-tax-lady.png" alt="Tiny Tax Lady" className="h-24 w-24 rounded-full object-cover ring-2 ring-white/70 shadow-sm" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                The Process
              </h2>
              <p className="text-muted-foreground text-lg">Simple steps from start to finish</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <Card 
                  key={index} 
                  className="group relative text-center p-6 hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:-translate-y-3 border-2 hover:border-primary/50 bg-gradient-to-br from-card via-card/80 to-card/50 animate-fade-in overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Sparkle decorations */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full animate-twinkle"></div>
                  <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-secondary rounded-full animate-twinkle animation-delay-2000"></div>
                  
                  {/* Shine sweep */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  {/* Radial glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary via-primary to-secondary text-white text-2xl font-bold mb-4 shadow-xl group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 group-hover:shadow-primary/50">
                      {step.number}
                    </div>
                    <h3 className="font-semibold text-xl mb-2 group-hover:text-primary transition-colors">{step.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/30 via-background to-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 animate-fade-in">
              <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full mb-4">
                <HelpCircle className="h-12 w-12 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent">
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="border-2 rounded-lg px-6 bg-gradient-to-br from-card to-card/50 hover:border-secondary/30 transition-all duration-300 hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-secondary transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-secondary via-secondary to-primary relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/cta-ready-to-start.jpg" 
            alt="Welcoming professional tax office entrance" 
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-secondary/40 via-secondary/40 to-primary/40" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.05),transparent_50%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold animate-fade-in">
              Still Have Questions?
            </h2>
            <p className="text-xl text-white/90 animate-fade-in" style={{ animationDelay: "100ms" }}>
              Let's talk! Book your appointment and we'll discuss everything you need.
            </p>
            <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
              <Button asChild size="lg" variant="secondary" className="text-lg shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-float">
                <Link to="/schedule-appointment">Book Your Appointment Now</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FAQ;
